import type { ComponentType } from 'react';
import { CamundaFormsService } from '@/lib/camunda/forms-service';
import { CamundaHistoryService } from '@/lib/camunda/history-service';
import { isHistoricFileReference } from '@/lib/camunda/form-file-values';
import { documentVersionSchema } from '@/forms/shared/components/document-version-schema';
import {
  getAvailableForms,
  getDefaultValuesFromSchema,
  loadHistoricFormSchema,
  loadHistoricTaskFormComponent,
  type TaskFormProps,
} from '@/lib/form-utilities';
import type { CamundaFormValue, CamundaFormValues } from '@/lib/camunda/types';
import type { HistoricTaskInstance } from '@/lib/camunda/history-types';
import type { BaseFormSchema } from '@/types/zod-schema';
import {
  getLaboralHistoricAdditionalVariableNames,
  getLaboralHistoricSchemaKeys,
} from '@/forms/laboral/historic-schema-dependencies';
import {
  getCommercialApprovalHistoricAdditionalVariableNames,
  getCommercialApprovalHistoricSchemaDependencies,
} from '@/forms/comercial/aprobacion-comercial/historic-schema-dependencies';

const HISTORIC_FORM_UNSUPPORTED_VERSION = 'HISTORIC_FORM_UNSUPPORTED_VERSION';
const HISTORIC_FORM_FORBIDDEN = 'HISTORIC_FORM_FORBIDDEN';
const VERSIONED_DOCUMENT_VARIABLE_NAMES = new Set([
  'documentos_valoracion',
  'signature_document_versions',
]);

function bindHistoricDocumentReferences(
  value: CamundaFormValue,
  variables: CamundaFormValues,
  unreadableVariableNames: readonly string[],
): CamundaFormValue {
  if (value == null) return value;
  if (!Array.isArray(value)) {
    throw new Error('La colección documental histórica no es válida');
  }

  return value.map((version) => {
    if (!(version instanceof Object) || Array.isArray(version)) {
      throw new Error('La versión documental histórica no es válida');
    }
    const { fileVariableName } = documentVersionSchema.parse(version);
    if (!fileVariableName) return version;
    if (unreadableVariableNames.includes(fileVariableName)) {
      throw new Error(`No se pudieron leer las variables históricas: ${fileVariableName}`);
    }
    const reference = variables[fileVariableName];
    if (!isHistoricFileReference(reference)) {
      throw new Error(`No se encontró una referencia de archivo histórica válida: ${fileVariableName}`);
    }
    return { ...version, historicReference: reference };
  });
}

type HistoricFormErrorCode =
  | typeof HISTORIC_FORM_UNSUPPORTED_VERSION
  | typeof HISTORIC_FORM_FORBIDDEN;

const hasHistoricFormErrorCode = <Value>(
  error: Value,
  code: HistoricFormErrorCode,
): error is Value & { readonly code: HistoricFormErrorCode } =>
  error instanceof Error && 'code' in error && error.code === code;

export class HistoricFormUnsupportedVersionError extends Error {
  readonly code = HISTORIC_FORM_UNSUPPORTED_VERSION;

  constructor() {
    super('Este formulario pertenece a una versión antigua que ya no está disponible.');
    this.name = 'HistoricFormUnsupportedVersionError';
  }
}

export function isHistoricFormUnsupportedVersionError<Value>(
  error: Value,
): error is Value & HistoricFormUnsupportedVersionError {
  return (
    error instanceof HistoricFormUnsupportedVersionError
    || hasHistoricFormErrorCode(error, HISTORIC_FORM_UNSUPPORTED_VERSION)
  );
}

export class HistoricFormForbiddenError extends Error {
  readonly code = HISTORIC_FORM_FORBIDDEN;

  constructor() {
    super('No autorizado para ver este formulario histórico');
    this.name = 'HistoricFormForbiddenError';
  }
}

export function isHistoricFormForbiddenError<Value>(
  error: Value,
): error is Value & HistoricFormForbiddenError {
  return (
    error instanceof HistoricFormForbiddenError
    || hasHistoricFormErrorCode(error, HISTORIC_FORM_FORBIDDEN)
  );
}

export interface HistoricFormData {
  formKey: string;
  FormComponent: ComponentType<TaskFormProps>;
  schema: BaseFormSchema;
  variables: CamundaFormValues;
  preloadedData: null;
}

export async function loadHistoricFormData(
  task: HistoricTaskInstance,
  processInstanceId: string,
  formKey: string | null,
): Promise<HistoricFormData> {
  if (!formKey || !getAvailableForms().includes(formKey)) {
    throw new HistoricFormUnsupportedVersionError();
  }

  const historicSchemaKeys = [
    ...getCommercialApprovalHistoricSchemaDependencies(formKey),
    ...getLaboralHistoricSchemaKeys(formKey),
  ];
  const [FormComponent, historicSchemas] = await Promise.all([
    loadHistoricTaskFormComponent(formKey),
    Promise.all(historicSchemaKeys.map(loadHistoricFormSchema)),
  ]);
  if (!FormComponent) {
    throw new HistoricFormUnsupportedVersionError();
  }
  const schema = historicSchemas.at(-1);
  if (!schema) {
    throw new HistoricFormUnsupportedVersionError();
  }

  const renderedVariableNames = new Set(
    [
      ...historicSchemas.flatMap((historicSchema) =>
        CamundaFormsService.extractVariableNamesFromSchema(historicSchema),
      ),
      ...getLaboralHistoricAdditionalVariableNames(formKey),
      ...getCommercialApprovalHistoricAdditionalVariableNames(formKey),
    ],
  );
  const participationResult =
    await CamundaHistoryService.fetchParticipationHistoricFormVariables(
      processInstanceId,
      task.id,
    );

  if (participationResult.status === 'forbidden') {
    throw new HistoricFormForbiddenError();
  }
  if (participationResult.status === 'not_found') {
    throw new Error('No se encontró la tarea o el proceso histórico solicitado');
  }
  if (participationResult.status === 'error') {
    throw new Error('No se pudieron cargar las variables del formulario histórico');
  }

  const unreadableSchemaVariableNames = participationResult.unreadableVariableNames.filter(
    (variableName) => renderedVariableNames.has(variableName),
  );
  if (unreadableSchemaVariableNames.length > 0) {
    throw new Error(
      `No se pudieron leer las variables históricas: ${unreadableSchemaVariableNames.join(', ')}`,
    );
  }

  const schemaVariables = Object.fromEntries(
    Object.entries(participationResult.variables).filter(([variableName]) =>
      renderedVariableNames.has(variableName),
    ).map(([variableName, value]) => [
      variableName,
      VERSIONED_DOCUMENT_VARIABLE_NAMES.has(variableName)
        ? bindHistoricDocumentReferences(
          value,
          participationResult.variables,
          participationResult.unreadableVariableNames,
        )
        : value,
    ]),
  );

  return {
    formKey,
    FormComponent,
    schema,
    variables: {
      ...getDefaultValuesFromSchema(schema),
      ...schemaVariables,
    },
    preloadedData: null,
  };
}
